// Ubicación en el repo: src/pages/PublicScheduling.jsx
// Auto-agendamiento público — /agendamiento-online (spec-final-autoagendamiento.md,
// DECISIONS.md #69-#71). Ruta pública, SIN sesión, montada fuera de
// RequireAuth/Layout en App.jsx — nunca debe depender de nada que exija
// login. Reimplementa el flujo de 5 pasos de prototipo-autoagendamiento.html
// (referencia de FLUJO, no de diseño visual final — sección 12 de la spec)
// contra las 4 Edge Functions públicas reales en vez de datos simulados.
//
// Todas las validaciones "duras" (RUT, disponibilidad real, límite de 2
// citas/RUT, captcha, conflicto de horario) se revalidan del lado del
// servidor en public-agendamiento-create — lo de acá es solo UX para
// fallar rápido antes de llamar a la red. Nunca confiar en el navegador.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchPublicConfig,
  lookupPatientByRut,
  fetchAvailability,
  createBooking,
  sanitizeRutClient,
  isValidRut,
  isValidBirthdateClient,
} from '../lib/publicScheduling';
import { applyClinicSettings, DEFAULT_SETTINGS } from '../lib/clinicSettings';
import { appointmentReceiptHTML } from '../lib/receipts';
import { printHTML } from '../lib/print';
import { formatDateLong, formatDate } from '../lib/format';
import { getPrivacyPolicySections } from '../lib/privacyPolicy';
import { getConsentSummarySections } from '../lib/consentDocument';

const TOTAL_STEPS = 5;
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

// Mismo TYPE_CONFIG que public-agendamiento-availability/-create (Deno) —
// se duplica acá porque el frontend no comparte módulos con las Edge
// Functions, igual que ORL_ROLES/OFT_ROLES ya se duplican entre funciones.
// Si esto cambia alguna vez, hay que actualizar los tres lugares.
const TIPOS_ATENCION = [
  { id: 'Audiometría', nombre: 'Audiometría', modulo: 'ORL', duracion: 30 },
  { id: 'Receta de lentes', nombre: 'Receta de lentes (Visiorefracción)', modulo: 'OFT', duracion: 15 },
];

const SEXO_OPCIONES = ['Femenino', 'Masculino', 'No binario', 'Prefiere no informar'];

function pad(n) {
  return String(n).padStart(2, '0');
}
function isoOf(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Accesibilidad (sección 11 de la spec): tarjetas/celdas clickeables
// también alcanzables por teclado (Tab + Enter/Espacio), sin depender del
// mouse — mismo criterio que hazAccesibleATeclado() del prototipo.
function a11yProps(onActivate) {
  return {
    role: 'button',
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
  };
}

// IMPORTANTE (encontrado 20-ago-2026, probando en producción): desde que
// Google movió la administración de reCAPTCHA a Google Cloud Console
// (DECISIONS.md #72), toda clave nueva — aunque se elija "casilla de
// verificación" (v2) al registrarla — exige el script `enterprise.js` y
// el namespace `grecaptcha.enterprise.*` del lado del navegador. El script
// clásico `api.js` + `grecaptcha.render()` (lo que se usaba acá antes)
// carga sin ningún error visible, pero nunca muestra el widget ni dispara
// nada — falla en silencio total. La verificación del lado del servidor
// (public-agendamiento-create, con la Secret key) NO cambia — Google
// mantiene compatibilidad ahí. Confirmado contra la documentación oficial:
// https://docs.cloud.google.com/recaptcha/docs/api-ref-checkbox-keys
function useRecaptchaWidget(onVerify, onExpire) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    let cancelled = false;

    function renderWidget() {
      if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
      if (!window.grecaptcha?.enterprise?.render) return;
      widgetIdRef.current = window.grecaptcha.enterprise.render(containerRef.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        callback: onVerify,
        'expired-callback': onExpire,
      });
      setReady(true);
    }

    // grecaptcha.enterprise.ready() es el patrón recomendado por Google —
    // encola la función si la librería todavía no terminó de inicializar,
    // en vez de confiar únicamente en el evento 'load' del <script>.
    function whenReady() {
      if (window.grecaptcha?.enterprise?.ready) {
        window.grecaptcha.enterprise.ready(renderWidget);
      } else {
        renderWidget();
      }
    }

    if (window.grecaptcha?.enterprise) {
      whenReady();
      return () => {
        cancelled = true;
      };
    }

    let script = document.getElementById('recaptcha-script');
    if (!script) {
      script = document.createElement('script');
      script.id = 'recaptcha-script';
      script.src = 'https://www.google.com/recaptcha/enterprise.js';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', whenReady);
    return () => {
      cancelled = true;
      script.removeEventListener('load', whenReady);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { containerRef, ready };
}

export default function PublicScheduling() {
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState('');

  const [form, setForm] = useState({
    rut: '',
    nombre: '',
    segundoNombre: '',
    apellido: '',
    segundoApellido: '',
    birthdate: '',
    sexo: '',
    phone: '+569',
    email: '',
  });
  const [rutStatus, setRutStatus] = useState(null); // 'checking' | 'found' | 'new' | 'invalid' | null
  const [rutLocked, setRutLocked] = useState(false); // true si vino de un match: nombre/apellido/rut/fnac/sexo no editables

  const [branchId, setBranchId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [professionalId, setProfessionalId] = useState('');
  const [professionals, setProfessionals] = useState([]);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  // Evita un parpadeo de "sin horas disponibles" en el primer render del
  // paso 4, antes de que el useEffect alcance a disparar la primera
  // consulta (loading arranca en false) — el aviso de "sin horas" solo
  // debe mostrarse después de terminar al menos una consulta real.
  const [hasFetchedAvailability, setHasFetchedAvailability] = useState(false);
  const [calMonth, setCalMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [conflictMessage, setConflictMessage] = useState('');

  const [consent, setConsent] = useState(false);
  // Footer legal (política de privacidad + consentimiento informado) —
  // pedido de Nicolás (22-ago-2026, ver DECISIONS.md): un solo estado
  // controla los dos, así que desplegar cualquiera de los dos links del
  // footer despliega ambos documentos juntos, no uno por separado.
  const [legalOpen, setLegalOpen] = useState(false);
  const legalFooterRef = useRef(null);
  function openLegalFooter() {
    setLegalOpen(true);
    legalFooterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  const [captchaToken, setCaptchaToken] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null); // respuesta de createBooking al confirmar

  const submittingRef = useRef(false);

  useEffect(() => {
    fetchPublicConfig()
      .then((data) => {
        setConfig(data);
        applyClinicSettings({ ...DEFAULT_SETTINGS, primaryColor: data.primaryColor, darkMode: false });
      })
      .catch((err) => setConfigError(err.message || 'No se pudo cargar la página. Intenta de nuevo más tarde.'));
  }, []);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // Autocompletado por RUT (sección 4.2) — debounced, no dispara una
  // consulta por cada tecla.
  const rutDebounceRef = useRef(null);
  function handleRutChange(raw) {
    const clean = sanitizeRutClient(raw);
    setField('rut', clean);
    setRutLocked(false);
    setRutStatus(null);
    if (rutDebounceRef.current) clearTimeout(rutDebounceRef.current);
    if (clean.length < 8) return;
    if (!isValidRut(clean)) {
      setRutStatus('invalid');
      return;
    }
    rutDebounceRef.current = setTimeout(async () => {
      setRutStatus('checking');
      try {
        const res = await lookupPatientByRut(clean);
        if (res.found) {
          const p = res.patient;
          setForm((f) => ({
            ...f,
            nombre: p.nombre || '',
            segundoNombre: p.segundoNombre || '',
            apellido: p.apellido || '',
            segundoApellido: p.segundoApellido || '',
            birthdate: p.birthdate || '',
            sexo: p.sexo || '',
            phone: p.phone || '+569',
            email: p.email || '',
          }));
          setRutLocked(true);
          setRutStatus('found');
        } else {
          setRutStatus('new');
        }
      } catch {
        // Si falla la consulta (red, etc.), no bloqueamos el formulario —
        // el paciente puede seguir completando a mano; la protección real
        // contra duplicados sigue siendo el índice único del backend.
        setRutStatus(null);
      }
    }, 400);
  }

  const [step1Error, setStep1Error] = useState('');
  function handleStep1Next() {
    setStep1Error('');
    if (!form.nombre.trim() || !form.apellido.trim() || !form.birthdate || !form.phone.trim() || !form.email.trim() || !form.sexo) {
      setStep1Error('Completa todos los campos obligatorios (nombre, apellido, RUT, fecha de nacimiento, sexo, teléfono y correo).');
      return;
    }
    if (form.rut.length < 8 || !isValidRut(form.rut)) {
      setStep1Error('El RUT ingresado no es válido — revisa el número y el dígito verificador.');
      return;
    }
    if (!isValidBirthdateClient(form.birthdate)) {
      setStep1Error('Revisa la fecha de nacimiento ingresada — no puede ser futura ni parecer inválida.');
      return;
    }
    setStep(2);
  }

  function handleSelectBranch(id) {
    setBranchId(id);
    setTypeId('');
    setProfessionalId('');
    setDate(null);
    setTime(null);
  }

  function handleSelectType(id) {
    setTypeId(id);
    setProfessionalId('');
    setDate(null);
    setTime(null);
    setSlotsByDate({});
    setHasFetchedAvailability(false);
  }

  // Disponibilidad: se pide de nuevo cada vez que cambia el profesional
  // elegido (o al entrar al paso 4) — la misma Edge Function calcula tanto
  // las horas libres como la lista de profesionales elegibles para este
  // módulo/sucursal.
  const loadAvailability = useCallback(
    async (profId) => {
      if (!branchId || !typeId) return;
      setAvailabilityLoading(true);
      setAvailabilityError('');
      try {
        const data = await fetchAvailability({ branchId, type: typeId, professionalId: profId || undefined });
        setSlotsByDate(data.slotsByDate || {});
        setProfessionals(data.professionals || []);
      } catch (err) {
        setAvailabilityError(err.message || 'No se pudo cargar la disponibilidad.');
      } finally {
        setAvailabilityLoading(false);
        setHasFetchedAvailability(true);
      }
    },
    [branchId, typeId]
  );

  useEffect(() => {
    if (step === 4 && branchId && typeId) {
      loadAvailability(professionalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, branchId, typeId]);

  function handleProfessionalChange(id) {
    setProfessionalId(id);
    setDate(null);
    setTime(null);
    loadAvailability(id);
  }

  function handlePickDate(iso) {
    setDate(iso);
    setTime(null);
  }

  async function handleConfirm() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setConfirming(true);
    setConfirmError('');
    try {
      const tipo = TIPOS_ATENCION.find((t) => t.id === typeId);
      const payload = {
        idempotencyKey: crypto.randomUUID(),
        branchId,
        type: typeId,
        professionalId: professionalId || null,
        date,
        time,
        patient: {
          rut: form.rut,
          nombre: form.nombre.trim(),
          segundoNombre: form.segundoNombre.trim() || undefined,
          apellido: form.apellido.trim(),
          segundoApellido: form.segundoApellido.trim() || undefined,
          birthdate: form.birthdate,
          sexo: form.sexo,
          phone: form.phone.trim(),
          email: form.email.trim(),
        },
        consent,
        recaptchaToken: captchaToken,
      };
      const data = await createBooking(payload);
      setResult({ ...data, tipo });
      setStep(6);
    } catch (err) {
      if (err.conflict) {
        setConflictMessage('No perdiste tus datos — solo elige otro horario disponible en el calendario.');
        setTime(null);
        setStep(4);
        loadAvailability(professionalId);
      } else {
        setConfirmError(err.message || 'No se pudo confirmar tu hora. Intenta de nuevo.');
      }
      // El captcha de Google es de un solo uso — si falló la confirmación
      // (por cualquier motivo), hay que resolverlo de nuevo antes de
      // reintentar.
      setCaptchaToken('');
      if (window.grecaptcha?.enterprise?.reset) window.grecaptcha.enterprise.reset();
    } finally {
      submittingRef.current = false;
      setConfirming(false);
    }
  }

  function handlePrintReceipt() {
    if (!result) return;
    const sucursal = config.branches.find((b) => b.id === branchId);
    const apptForReceipt = {
      date,
      time,
      type: typeId,
      patients: { nombre: form.nombre, segundo_nombre: form.segundoNombre, apellido: form.apellido, segundo_apellido: form.segundoApellido },
      professionals: result.professional || null,
    };
    printHTML(
      'Comprobante de cita',
      appointmentReceiptHTML(
        apptForReceipt,
        { name: sucursal?.name, address: sucursal?.address, mapsLinkEnabled: sucursal?.mapsLinkEnabled },
        sucursal?.logoUrl
      )
    );
  }

  function handleRestart() {
    setStep(1);
    setForm({ rut: '', nombre: '', segundoNombre: '', apellido: '', segundoApellido: '', birthdate: '', sexo: '', phone: '+569', email: '' });
    setRutStatus(null);
    setRutLocked(false);
    setBranchId('');
    setTypeId('');
    setProfessionalId('');
    setDate(null);
    setTime(null);
    setConsent(false);
    setCaptchaToken('');
    setResult(null);
  }

  if (configError) {
    return (
      <div className="pubsched-page">
        <PublicTopbar />
        <div className="pubsched-wrap">
          <div className="pubsched-card">
            <div className="pubsched-notice danger">{configError}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="pubsched-page">
        <PublicTopbar />
        <div className="pubsched-wrap pubsched-loading">
          <div className="pubsched-loading-inline" style={{ justifyContent: 'center' }}>
            <Spinner dark />
            Cargando…
          </div>
        </div>
      </div>
    );
  }

  return (
    // Fragment a propósito: #print-area va COMO HERMANO de .pubsched-page,
    // no adentro. Bug encontrado 20-ago-2026 (comprobante imprimía en
    // blanco): .pubsched-page tiene `display:none !important` en
    // @media print (index.css) para no imprimir el resto de la página —
    // pero un `display:none` en el padre oculta a los hijos sin
    // excepción, así que #print-area (que sí necesita `display:block` en
    // @media print) quedaba invisible igual por estar anidado adentro.
    <>
      <div className="pubsched-page">
        <PublicTopbar />
      <div className="pubsched-wrap">
        {step <= TOTAL_STEPS && (
          <div className="pubsched-progress">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((i) => (
              <div key={i} className={`seg ${i < step ? 'done' : i === step ? 'current' : ''}`} />
            ))}
          </div>
        )}

        {step === 1 && (
          <Step1PatientData
            form={form}
            setField={setField}
            onRutChange={handleRutChange}
            rutStatus={rutStatus}
            rutLocked={rutLocked}
            error={step1Error}
            onNext={handleStep1Next}
          />
        )}
        {step === 2 && (
          <Step2Branch branches={config.branches} branchId={branchId} onSelect={handleSelectBranch} onBack={() => setStep(1)} onNext={() => setStep(3)} />
        )}
        {step === 3 && (
          <Step3Type
            branch={config.branches.find((b) => b.id === branchId)}
            typeId={typeId}
            onSelect={handleSelectType}
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
          />
        )}
        {step === 4 && (
          <Step4DateTime
            tipo={TIPOS_ATENCION.find((t) => t.id === typeId)}
            sucursal={config.branches.find((b) => b.id === branchId)}
            professionals={professionals}
            professionalId={professionalId}
            onProfessionalChange={handleProfessionalChange}
            slotsByDate={slotsByDate}
            loading={availabilityLoading}
            hasFetchedOnce={hasFetchedAvailability}
            error={availabilityError}
            calMonth={calMonth}
            setCalMonth={setCalMonth}
            date={date}
            time={time}
            onPickDate={handlePickDate}
            onPickTime={setTime}
            conflictMessage={conflictMessage}
            clearConflict={() => setConflictMessage('')}
            onBack={() => setStep(3)}
            onNext={() => setStep(5)}
          />
        )}
        {step === 5 && (
          <Step5Review
            form={form}
            sucursal={config.branches.find((b) => b.id === branchId)}
            tipo={TIPOS_ATENCION.find((t) => t.id === typeId)}
            profesional={professionals.find((p) => p.id === professionalId)}
            legal={config.legal}
            date={date}
            time={time}
            consent={consent}
            setConsent={setConsent}
            onOpenLegal={openLegalFooter}
            captchaToken={captchaToken}
            setCaptchaToken={setCaptchaToken}
            error={confirmError}
            confirming={confirming}
            onBack={() => setStep(4)}
            onConfirm={handleConfirm}
          />
        )}
        {step === 6 && result && (
          <StepSuccess
            sucursal={config.branches.find((b) => b.id === branchId)}
            tipo={result.tipo}
            email={form.email}
            date={date}
            time={time}
            emailSent={result.emailSent}
            onPrint={handlePrintReceipt}
            onRestart={handleRestart}
          />
        )}
      </div>
      <LegalFooter legal={config.legal} open={legalOpen} setOpen={setLegalOpen} footerRef={legalFooterRef} />
      </div>
      <div id="print-area" />
    </>
  );
}

// Footer legal (22-ago-2026, ver DECISIONS.md) — pedido de Nicolás: en vez
// del acordeón suelto que vivía dentro del paso 5, la Política de Privacidad
// y el Consentimiento Informado ahora viven acá, al final de la página
// (mismo lugar donde cualquier sitio los pone), visibles en cualquier paso.
// Un solo estado (`open`, controlado por el padre) hace que desplegar
// cualquiera de los dos links despliegue AMBOS documentos juntos, no uno
// por separado — así el paciente tiene acceso a los dos de una sola vez.
function LegalFooter({ legal, open, setOpen, footerRef }) {
  const privacySections = getPrivacyPolicySections(legal);
  const consentSections = getConsentSummarySections(legal);

  return (
    <div className="pubsched-legal-footer" ref={footerRef}>
      <div className="pubsched-legal-footer__links">
        <button type="button" className="pubsched-legal-footer__link" onClick={() => setOpen((o) => !o)}>
          📄 Política de Privacidad
        </button>
        <button type="button" className="pubsched-legal-footer__link" onClick={() => setOpen((o) => !o)}>
          📝 Consentimiento informado y auto-agendamiento
        </button>
      </div>
      {open && (
        <div className="pubsched-legal-footer__panel">
          <div className="pubsched-legal-footer__doc">
            <h3>Política de Privacidad</h3>
            {privacySections.map((s) => (
              <div key={s.title} className="pubsched-legal-footer__section">
                <h4>{s.title}</h4>
                {s.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            ))}
          </div>
          <div className="pubsched-legal-footer__doc">
            <h3>Consentimiento informado y auto-agendamiento</h3>
            {consentSections.map((s) => (
              <div key={s.title} className="pubsched-legal-footer__section">
                <h4>{s.title}</h4>
                {s.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Spinner chico y reutilizable (23-ago-2026, ronda estética) — `dark` para
// usarlo sobre fondo claro (dentro de una tarjeta), sin la variante se
// asume que va sobre un botón de color (texto blanco).
function Spinner({ dark }) {
  return <span className={`pubsched-spinner ${dark ? 'dark' : ''}`} aria-hidden="true" />;
}

// Mismo header que la página pública institucional (`PublicLanding.jsx`) —
// reutiliza sus clases `.landing-header*` a propósito, para que ambas
// páginas públicas se vean consistentes (23-ago-2026, pedido de Nicolás:
// "para ambas debería ser la misma"). Sin botón de acceso: esta pantalla
// es solo para pacientes agendando, "Acceso profesionales" no aplica acá.
function PublicTopbar() {
  return (
    <header className="landing-header">
      <div className="landing-header__brand">
        <strong>Agenda Comunal</strong>
        <span className="landing-header__sub">by GlobalVision</span>
      </div>
      <img src="/agendadock-logo.png" alt="AgendaDock" className="landing-header__powered-logo" />
    </header>
  );
}

/* ---------------- Paso 1: datos del paciente ---------------- */
function Step1PatientData({ form, setField, onRutChange, rutStatus, rutLocked, error, onNext }) {
  return (
    <div className="pubsched-step">
      <div className="pubsched-step-label">Paso 1 de 5</div>
      <h2>
        <span className="pubsched-icon" aria-hidden="true">
          📋
        </span>
        Tus datos
      </h2>
      <div className="pubsched-card">
        <label className="field">
          <span>RUT</span>
          <input type="text" value={form.rut} onChange={(e) => onRutChange(e.target.value)} placeholder="12345678-5" autoFocus />
          <span className="pubsched-field-hint">
            ¿Eres paciente extranjero o no tienes RUT chileno? Contáctanos directamente en tu sucursal para agendar.
          </span>
        </label>

        {rutStatus === 'checking' && <div className="pubsched-notice">Revisando…</div>}
        {rutStatus === 'invalid' && <div className="pubsched-notice warn">⚠ Este RUT no parece válido — revisa el número y el dígito verificador antes de continuar.</div>}
        {rutStatus === 'found' && (
          <div className="pubsched-notice">
            ✓ Encontramos tu registro. Ya completamos tus datos — puedes actualizar tu <strong>teléfono</strong> y{' '}
            <strong>correo</strong> directamente aquí abajo. Si tu nombre, RUT, fecha de nacimiento o sexo están incorrectos,
            corrígelo en tu próxima visita a la sucursal.
          </div>
        )}
        {rutStatus === 'new' && <div className="pubsched-notice">No encontramos un registro previo con este RUT — se creará una ficha nueva al confirmar tu hora.</div>}

        <div className="pubsched-row2">
          <label className="field">
            <span>Nombre</span>
            <input type="text" value={form.nombre} onChange={(e) => setField('nombre', e.target.value)} disabled={rutLocked} />
          </label>
          <label className="field">
            <span>
              Segundo nombre <span style={{ fontWeight: 400 }}>(opcional)</span>
            </span>
            <input type="text" value={form.segundoNombre} onChange={(e) => setField('segundoNombre', e.target.value)} disabled={rutLocked} />
          </label>
        </div>
        <div className="pubsched-row2">
          <label className="field">
            <span>Apellido</span>
            <input type="text" value={form.apellido} onChange={(e) => setField('apellido', e.target.value)} disabled={rutLocked} />
          </label>
          <label className="field">
            <span>
              Segundo apellido <span style={{ fontWeight: 400 }}>(opcional)</span>
            </span>
            <input type="text" value={form.segundoApellido} onChange={(e) => setField('segundoApellido', e.target.value)} disabled={rutLocked} />
          </label>
        </div>
        <div className="pubsched-row2">
          <label className="field">
            <span>Fecha de nacimiento</span>
            <input type="date" value={form.birthdate} onChange={(e) => setField('birthdate', e.target.value)} disabled={rutLocked} />
          </label>
          <label className="field">
            <span>Sexo</span>
            <select value={form.sexo} onChange={(e) => setField('sexo', e.target.value)} disabled={rutLocked}>
              <option value="">Selecciona</option>
              {SEXO_OPCIONES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="pubsched-row2">
          <label className="field">
            <span>Teléfono</span>
            <input type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
          </label>
          <label className="field">
            <span>Correo</span>
            <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
          </label>
        </div>

        {error && <div className="pubsched-notice danger">{error}</div>}

        <div className="pubsched-btn-row">
          <span />
          <button type="button" className="button-primary pubsched-btn-wide" onClick={onNext}>
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Paso 2: sucursal ---------------- */
function Step2Branch({ branches, branchId, onSelect, onBack, onNext }) {
  return (
    <div className="pubsched-step">
      <div className="pubsched-step-label">Paso 2 de 5</div>
      <h2>
        <span className="pubsched-icon" aria-hidden="true">
          🏥
        </span>
        ¿En qué sucursal te atenderás?
      </h2>
      <div className="pubsched-card">
        <div className="pubsched-option-grid">
          {branches.map((b) => (
            <div key={b.id} className={`pubsched-option-card ${branchId === b.id ? 'selected' : ''}`} {...a11yProps(() => onSelect(b.id))}>
              {b.logoUrl ? (
                <img src={b.logoUrl} alt="" className="pubsched-option-card__logo" />
              ) : (
                <div className="imgPlaceholder">{b.name}</div>
              )}
              <div className="title">{b.name}</div>
              {b.address && (
                <div className="desc">
                  <span className="pubsched-icon" aria-hidden="true">
                    📍
                  </span>
                  {b.address}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="pubsched-btn-row">
          <button type="button" className="pubsched-btn-ghost" onClick={onBack}>
            Volver
          </button>
          <button type="button" className="button-primary pubsched-btn-wide" onClick={onNext} disabled={!branchId}>
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Paso 3: tipo de atención ---------------- */
function Step3Type({ branch, typeId, onSelect, onBack, onNext }) {
  if (!branch) return null;
  const conModuloActivo = TIPOS_ATENCION.filter((t) => (t.modulo === 'ORL' ? branch.orlEnabled : branch.oftEnabled));
  const disponibles = conModuloActivo.filter((t) => (t.modulo === 'ORL' ? branch.hasOrlProfessional : branch.hasOftProfessional));
  const proximamente = conModuloActivo.filter((t) => !disponibles.includes(t));

  return (
    <div className="pubsched-step">
      <div className="pubsched-step-label">Paso 3 de 5</div>
      <h2>
        <span className="pubsched-icon" aria-hidden="true">
          🩺
        </span>
        ¿Qué atención necesitas?
      </h2>
      <div className="pubsched-card">
        <div className="pubsched-notice">Mostrando solo las atenciones disponibles en {branch.name}, según los módulos activos de esta sucursal.</div>
        <div className="pubsched-option-grid">
          {disponibles.map((t) => (
            <div key={t.id} className={`pubsched-option-card ${typeId === t.id ? 'selected' : ''}`} {...a11yProps(() => onSelect(t.id))}>
              <div className="imgPlaceholder">⏱ {t.duracion} min</div>
              <div className="title">{t.nombre}</div>
              <div className="desc">Duración estimada: {t.duracion} minutos</div>
            </div>
          ))}
        </div>
        {proximamente.map((t) => (
          <div key={t.id} className="pubsched-notice warn" style={{ marginTop: 12 }}>
            {t.nombre}: pronto podrás agendar esta atención en {branch.name}. Revisa en 24 horas o contáctanos directamente.
          </div>
        ))}
        <div className="pubsched-btn-row">
          <button type="button" className="pubsched-btn-ghost" onClick={onBack}>
            Volver
          </button>
          <button type="button" className="button-primary pubsched-btn-wide" onClick={onNext} disabled={!typeId}>
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Paso 4: mini-calendario + hora + profesional ---------------- */
function MiniCalendar({ calMonth, setCalMonth, slotsByDate, date, onPickDate }) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const maxDate = new Date(hoy);
  maxDate.setDate(maxDate.getDate() + 30);

  const monthLabel = calMonth.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
  const firstOfMonth = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
  let leading = firstOfMonth.getDay() - 1;
  if (leading < 0) leading = 6;
  const daysInMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate();

  const firstOfPrevMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1);
  const lastOfPrevMonth = new Date(calMonth.getFullYear(), calMonth.getMonth(), 0);
  const firstOfNextMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1);
  const prevDisabled = lastOfPrevMonth < hoy;
  const nextDisabled = firstOfNextMonth > maxDate;

  const cells = [];
  for (let i = 0; i < leading; i++) cells.push(<div key={`e${i}`} className="pubsched-calday empty" />);
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(calMonth.getFullYear(), calMonth.getMonth(), day);
    d.setHours(0, 0, 0, 0);
    const iso = isoOf(d);
    const dentroDeRango = d >= hoy && d <= maxDate;
    const horas = slotsByDate[iso] || [];
    const tieneHoras = dentroDeRango && horas.length > 0;
    const isToday = iso === isoOf(new Date());
    const isSelected = date === iso;
    cells.push(
      <div
        key={iso}
        className={`pubsched-calday ${tieneHoras ? 'enabled' : 'disabled'} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
        {...(tieneHoras ? a11yProps(() => onPickDate(iso)) : {})}
      >
        <span>{day}</span>
        {tieneHoras && <span className="dot avail" />}
      </div>
    );
  }

  return (
    <div className="pubsched-minical">
      <div className="calHead">
        <button type="button" className="calNav" disabled={prevDisabled} onClick={() => setCalMonth(firstOfPrevMonth)}>
          ‹
        </button>
        <div className="label">{monthLabel}</div>
        <button type="button" className="calNav" disabled={nextDisabled} onClick={() => setCalMonth(firstOfNextMonth)}>
          ›
        </button>
      </div>
      <div className="pubsched-calgrid">
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d) => (
          <div key={d} className="pubsched-caldow">
            {d}
          </div>
        ))}
        {cells}
      </div>
      <div className="pubsched-cal-legend">
        <div className="row">
          <span className="dot" /> Con horas disponibles — puedes elegir este día
        </div>
        <div className="row">Sin punto = agenda completa o fuera del rango de 30 días</div>
      </div>
    </div>
  );
}

function Step4DateTime({
  tipo,
  sucursal,
  professionals,
  professionalId,
  onProfessionalChange,
  slotsByDate,
  loading,
  hasFetchedOnce,
  error,
  calMonth,
  setCalMonth,
  date,
  time,
  onPickDate,
  onPickTime,
  conflictMessage,
  clearConflict,
  onBack,
  onNext,
}) {
  const hayAlgo = Object.keys(slotsByDate).length > 0;

  if (hasFetchedOnce && !loading && !error && !hayAlgo) {
    return (
      <div className="pubsched-step">
        <div className="pubsched-step-label">Paso 4 de 5</div>
        <h2>
          <span className="pubsched-icon" aria-hidden="true">
            📅
          </span>
          Elige fecha y hora
        </h2>
        <div className="pubsched-card">
          <div className="pubsched-conflict-banner">
            <div className="icon">📅</div>
            <div>
              <div className="title">Sin horas disponibles por ahora</div>
              <div>
                {sucursal?.name} no tiene horas libres para {tipo?.nombre.toLowerCase()} en los próximos 30 días. Contáctanos
                directamente a la sucursal para revisar otras alternativas.
              </div>
            </div>
          </div>
          <div className="pubsched-btn-row">
            <button type="button" className="pubsched-btn-ghost" onClick={onBack}>
              Volver
            </button>
            <span />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pubsched-step">
      <div className="pubsched-step-label">Paso 4 de 5</div>
      <h2>
        <span className="pubsched-icon" aria-hidden="true">
          📅
        </span>
        Elige fecha y hora
      </h2>
      <div className="pubsched-card">
        {conflictMessage && (
          <div className="pubsched-conflict-banner">
            <div className="icon">⚠</div>
            <div>
              <div className="title">Esa hora ya no está disponible</div>
              <div>{conflictMessage}</div>
            </div>
          </div>
        )}
        {error && <div className="pubsched-notice danger">{error}</div>}

        {professionals.length > 1 && (
          <label className="field">
            <span>Profesional</span>
            <select value={professionalId} onChange={(e) => onProfessionalChange(e.target.value)}>
              <option value="">Cualquiera disponible</option>
              {professionals.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} {p.apellido} — {p.role}
                </option>
              ))}
            </select>
            <span className="pubsched-field-hint">Si no eliges, te asignaremos el primer profesional disponible.</span>
          </label>
        )}

        <label className="field">
          <span>Fecha y hora</span>
        </label>
        <div className="pubsched-calslot-layout">
          <MiniCalendar
            calMonth={calMonth}
            setCalMonth={setCalMonth}
            slotsByDate={slotsByDate}
            date={date}
            onPickDate={(iso) => {
              clearConflict();
              onPickDate(iso);
            }}
          />
          <div className="pubsched-slot-panel">
            <div className="title">
              Horas disponibles — {sucursal?.name} · {tipo?.nombre}
            </div>
            {loading ? (
              <div className="pubsched-empty-slots">
                <div className="pubsched-loading-inline" style={{ justifyContent: 'center' }}>
                  <Spinner dark />
                  Cargando disponibilidad…
                </div>
              </div>
            ) : !date ? (
              <div className="pubsched-empty-slots">Elige una fecha en el calendario para ver las horas disponibles.</div>
            ) : (slotsByDate[date] || []).length === 0 ? (
              <div className="pubsched-empty-slots">No quedan horas disponibles ese día. Elige otra fecha en el calendario.</div>
            ) : (
              <div className="pubsched-slot-grid">
                {(slotsByDate[date] || []).map((h) => (
                  <div key={h} className={`pubsched-slot ${time === h ? 'selected' : ''}`} {...a11yProps(() => onPickTime(h))}>
                    {h}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="pubsched-btn-row">
          <button type="button" className="pubsched-btn-ghost" onClick={onBack}>
            Volver
          </button>
          <button type="button" className="button-primary pubsched-btn-wide" onClick={onNext} disabled={!date || !time}>
            Continuar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Paso 5: revisión + consentimiento + captcha ---------------- */
function Step5Review({ form, sucursal, tipo, profesional, date, time, consent, setConsent, onOpenLegal, captchaToken, setCaptchaToken, error, confirming, onBack, onConfirm }) {
  const nombreCompleto = [form.nombre, form.segundoNombre, form.apellido, form.segundoApellido].filter(Boolean).join(' ');
  const { containerRef } = useRecaptchaWidget(
    (token) => setCaptchaToken(token),
    () => setCaptchaToken('')
  );

  const canConfirm = consent && (RECAPTCHA_SITE_KEY ? !!captchaToken : false) && !confirming;

  return (
    <div className="pubsched-step">
      <div className="pubsched-step-label">Paso 5 de 5</div>
      <h2>
        <span className="pubsched-icon" aria-hidden="true">
          ✅
        </span>
        Revisa antes de confirmar
      </h2>
      <div className="pubsched-card">
        <div className="pubsched-summary-row">
          <span className="k">Nombre</span>
          <span className="v">{nombreCompleto}</span>
        </div>
        <div className="pubsched-summary-row">
          <span className="k">RUT</span>
          <span className="v">{form.rut}</span>
        </div>
        <div className="pubsched-summary-row">
          <span className="k">Fecha de nacimiento</span>
          <span className="v">{formatDate(form.birthdate)}</span>
        </div>
        <div className="pubsched-summary-row">
          <span className="k">Teléfono</span>
          <span className="v">{form.phone}</span>
        </div>
        <div className="pubsched-summary-row">
          <span className="k">Correo</span>
          <span className="v">{form.email}</span>
        </div>
        <div className="pubsched-summary-row">
          <span className="k">Sucursal</span>
          <span className="v">{sucursal?.name}</span>
        </div>
        <div className="pubsched-summary-row">
          <span className="k">Atención</span>
          <span className="v">{tipo?.nombre}</span>
        </div>
        <div className="pubsched-summary-row">
          <span className="k">Profesional</span>
          <span className="v">{profesional ? `${profesional.nombre} ${profesional.apellido} — ${profesional.role}` : 'Asignado automáticamente'}</span>
        </div>
        <div className="pubsched-summary-row">
          <span className="k">Fecha y hora</span>
          <span className="v">
            {formatDateLong(date)}, {time} hrs
          </span>
        </div>

        <div className="pubsched-notice">
          ¿Algo se ve incorrecto? Vuelve al paso 1: teléfono y correo puedes corregirlos tú mismo; nombre, RUT, fecha de
          nacimiento o sexo requieren que la sucursal los actualice.
        </div>

        <div className="pubsched-check-row">
          <input type="checkbox" id="pubsched-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <label htmlFor="pubsched-consent">
            Autorizo el tratamiento de mis datos personales y de salud para fines de auto-agendamiento y
            atención clínica, conforme a los{' '}
            <button type="button" className="pubsched-inline-link" onClick={onOpenLegal}>
              datos de consentimiento informado y política de privacidad
            </button>
            .
          </label>
        </div>

        {RECAPTCHA_SITE_KEY ? (
          <div style={{ marginTop: 12 }} ref={containerRef} />
        ) : (
          <div className="pubsched-notice warn" style={{ marginTop: 12 }}>
            La verificación "no soy un robot" todavía no está configurada en este sitio — contacta al equipo técnico antes de
            publicar esta página.
          </div>
        )}

        {error && <div className="pubsched-notice danger">{error}</div>}

        <div className="pubsched-btn-row">
          <button type="button" className="pubsched-btn-ghost" onClick={onBack}>
            Volver
          </button>
          <button type="button" className="button-primary pubsched-btn-wide" onClick={onConfirm} disabled={!canConfirm}>
            {confirming ? (
              <>
                <Spinner />
                Confirmando…
              </>
            ) : (
              'Confirmar hora'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Paso 6: confirmación ---------------- */
// El paso con más cuidado visual de todos (23-ago-2026, ronda estética,
// pedido explícito de Nicolás: "es el momento de mayor satisfacción del
// paciente"). El resumen pasó de un párrafo corrido a filas con ícono,
// mismo lenguaje visual que .pubsched-summary-row del paso 5, y el ✓ tiene
// una animación de entrada (.pubsched-success-icon, ver index.css).
function StepSuccess({ sucursal, tipo, email, date, time, emailSent, onPrint, onRestart }) {
  return (
    <div className="pubsched-card pubsched-success-wrap pubsched-step">
      <div className="pubsched-success-icon">✓</div>
      <h2>Hora confirmada</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Anotamos tu hora — este es el resumen:</p>

      <div className="pubsched-success-detail">
        <div className="pubsched-success-detail__rows">
          <div className="row">
            <span className="pubsched-icon" aria-hidden="true">
              🏥
            </span>
            <span>{sucursal?.name}</span>
          </div>
          <div className="row">
            <span className="pubsched-icon" aria-hidden="true">
              🩺
            </span>
            <span>{tipo?.nombre}</span>
          </div>
          <div className="row">
            <span className="pubsched-icon" aria-hidden="true">
              📅
            </span>
            <span>
              {formatDateLong(date)}, {time} hrs
            </span>
          </div>
        </div>
        {sucursal?.logoUrl && (
          <div className="pubsched-success-detail__logo">
            <img src={sucursal.logoUrl} alt={sucursal.name} />
          </div>
        )}
      </div>

      <div className="pubsched-link-note">
        {emailSent ? (
          <>
            <span className="pubsched-icon" aria-hidden="true">
              ✉️
            </span>
            Te enviamos un correo a <strong>{email}</strong> con el detalle de tu hora y un enlace para cancelarla si no puedes
            asistir. El enlace te llevará a una página donde deberás confirmar la cancelación con un clic — no se cancela
            automáticamente solo por abrir el correo.
          </>
        ) : (
          <>
            Tu hora quedó confirmada, pero no pudimos enviarte el correo con el detalle. Si necesitas cancelarla más adelante,
            contáctanos directamente en {sucursal?.name}.
          </>
        )}
      </div>
      <div className="pubsched-btn-row" style={{ justifyContent: 'center' }}>
        <button type="button" className="pubsched-btn-ghost" onClick={onPrint}>
          Imprimir comprobante
        </button>
        <button type="button" className="pubsched-btn-ghost" onClick={onRestart}>
          Agendar otra hora
        </button>
      </div>
    </div>
  );
}
